import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import type { TripTagImage } from "@przezmorze/api-types";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import { Select } from "../../components/select/Select";
import DictionaryAccordion from "./DictionaryAccordion";
import EditTripTagDialog from "./EditTripTagDialog";
import type { TripTag, TripTagCategory } from "./tripTagsApi";
import { useTripTags } from "./useTripTags";

const categories: TripTagCategory[] = [
  "Najpopularniejsze",
  "Kraj",
  "Na WWW",
  "Rodzaj grupy",
  "Transport",
  "Długość",
  "Inne",
];

interface AddTagProps {
  readonly onAdd: (
    category: TripTagCategory,
    name: string,
    isPopular: boolean,
    tagImage?: TripTagImage,
  ) => Promise<void>;
}

const AddTagRow: FC<AddTagProps> = ({ onAdd }) => {
  const [category, setCategory] = useState<TripTagCategory>("Najpopularniejsze");
  const [name, setName] = useState("");
  return (
    <TableBody>
      <TableRow>
        <TableCell>
          <Select
            fullWidth
            label="Kategoria"
            values={categories.map((value) => ({ value }))}
            value={category}
            onSelect={setCategory}
          />
        </TableCell>
        <TableCell>
          <TextField fullWidth label="Nazwa" value={name} onChange={(e) => setName(e.target.value)} />
        </TableCell>
        <TableCell>
          <AsyncLoadingButton
            disabled={!name}
            onClick={async () => {
              await onAdd(category, name, false);
              setCategory("Najpopularniejsze");
              setName("");
            }}
          >
            Dodaj
          </AsyncLoadingButton>
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface TagRowProps {
  readonly tag: TripTag;
  readonly onDelete: () => Promise<void>;
  readonly onUpdate: (
    category: TripTagCategory,
    name: string,
    isPopular: boolean,
    tagImage?: TripTagImage,
  ) => Promise<void>;
}

const TagRow: FC<TagRowProps> = ({ tag, onUpdate, onDelete }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <TableRow data-testid="tag-row">
      <TableCell>{tag.category}</TableCell>
      <TableCell>{tag.name}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={2}>
          <IconButton size="small" aria-label="Edytuj tag" onClick={() => setDialogOpen(true)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={onDelete} aria-label="Usuń tag ze słownika">
            <DeleteIcon />
          </IconButton>
        </Stack>
        <EditTripTagDialog
          open={dialogOpen}
          category={tag.category}
          tagName={tag.name}
          tagImage={tag.image}
          isTagPopular={tag.isPopular}
          onChange={onUpdate}
          onClose={() => setDialogOpen(false)}
        />
      </TableCell>
    </TableRow>
  );
};

const OrderTagDictionary = () => {
  const { tags, isLoading, addTag, updateTag, deleteTag } = useTripTags();
  return (
    <DictionaryAccordion title="Tagi zamówień" isLoading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Kategoria</TableCell>
            <TableCell>Nazwa</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <AddTagRow
          onAdd={async (c, n, isPopular, tagImage) => {
            await addTag(c, n, isPopular, tagImage);
          }}
        />
        <TableBody>
          {tags.map((tag) => (
            <TagRow
              key={tag.id}
              tag={tag}
              onUpdate={async (category, name, isPopular, tagImage) => {
                await updateTag(tag.id, category, name, isPopular, tagImage);
              }}
              onDelete={async () => {
                await deleteTag(tag.id);
              }}
            />
          ))}
        </TableBody>
      </Table>
    </DictionaryAccordion>
  );
};

export default OrderTagDictionary;
